import ExcelJS from 'exceljs';

const CURRENCY_FMT = '#,##0.00';
const PCT_FMT = '0.00%';
const DATE_FMT = 'yyyy-mm-dd';

// ─── Shared style constants ────────────────────────────────────────────────────

const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
const TOTAL_FILL  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E7FF' } };
const LABEL_FILL  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
/** Monthly closing Summary sheet — paired label/value tints */
const MC_SUM_L_START = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5F1' } };
const MC_SUM_V_START = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB8EBD9' } };
const MC_SUM_L_END = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0F2FE' } };
const MC_SUM_V_END = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBAE6FD' } };
const MC_SUM_L_EXP = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE4E6' } };
const MC_SUM_V_EXP = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFECDD3' } };
const MC_SUM_L_PUR = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF9C3' } };
const MC_SUM_V_PUR = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFDE68A' } };
const MC_SUM_L_REF = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDE9FE' } };
const MC_SUM_V_REF = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDD6FE' } };
const MC_SUM_L_SAL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCFCE7' } };
const MC_SUM_V_SAL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBBF7D0' } };
const MC_SUM_L_REV = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E7FF' } };
const MC_SUM_V_REV = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC7D2FE' } };
const CELL_BORDER = { style: 'thin', color: { argb: 'FFD1D5DB' } };
const ALL_BORDERS = { top: CELL_BORDER, bottom: CELL_BORDER, left: CELL_BORDER, right: CELL_BORDER };
const OUTLINE_BORDER = { style: 'medium', color: { argb: 'FF94A3B8' } };
/** Offer component block alternating background (skips unit-type/margin columns) */
const OFFER_BLOCK_ALT_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBFBFBF' } };
const CENTER      = { horizontal: 'center', vertical: 'middle' };

// ─── Shared style helpers ──────────────────────────────────────────────────────

function applyHeader(ws, headers, colWidths) {
  ws.columns = colWidths.map((w) => ({ width: w }));
  const row = ws.addRow(headers);
  row.height = 26;
  row.eachCell((cell) => {
    cell.font      = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.alignment = CENTER;
    cell.fill      = HEADER_FILL;
    cell.border    = ALL_BORDERS;
  });
  ws.views = [{ state: 'frozen', ySplit: 1 }];
}

function applyDataRow(row, numCols) {
  row.height = 20;
  for (let c = 1; c <= numCols; c++) {
    const cell = row.getCell(c);
    if (!cell.alignment) cell.alignment = CENTER;
    cell.border = ALL_BORDERS;
  }
}

function applyTotalRow(row, numCols) {
  row.height = 22;
  for (let c = 1; c <= numCols; c++) {
    const cell = row.getCell(c);
    cell.font      = { bold: true, size: 11 };
    cell.alignment = CENTER;
    cell.fill      = TOTAL_FILL;
    cell.border    = ALL_BORDERS;
  }
}

/** Key-value row used in summary sheets (label | value) */
function applyKVRow(ws, label, value, rowNum, opts = {}) {
  const r = ws.getRow(rowNum);
  r.height = opts.rowHeight ?? 24;
  const c1 = r.getCell(1);
  c1.value = label;
  c1.font =
    opts.labelFont ??
    { bold: true, size: 10.5, color: { argb: 'FF000000' } };
  c1.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  c1.fill = opts.labelFill ?? LABEL_FILL;
  c1.border = ALL_BORDERS;
  const c2 = r.getCell(2);
  c2.value = value;
  c2.font =
    opts.valueFont ??
    (opts.valueBold
      ? { bold: true, size: 11.5, color: { argb: 'FF000000' } }
      : { bold: false, size: 11, color: { argb: 'FF000000' } });
  c2.alignment = CENTER;
  c2.fill =
    opts.valueFill ??
    { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
  c2.border = ALL_BORDERS;
  if (opts.numFmt) c2.numFmt = opts.numFmt;
}

/** `#RGB` / `#RRGGBB` → Excel ARGB `FFRRGGBB`, or null if invalid. */
function cssHexToArgb(hex) {
  if (hex == null || hex === '') return null;
  let h = String(hex).trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return `FF${h.toUpperCase()}`;
}

function pickContrastingFontArgb(fillArgb) {
  const r = parseInt(fillArgb.slice(2, 4), 16);
  const g = parseInt(fillArgb.slice(4, 6), 16);
  const b = parseInt(fillArgb.slice(6, 8), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? 'FF1E293B' : 'FFFFFFFF';
}

/** Fixed fill for offer category cells in sales/refund exports. */
const OFFER_CATEGORY_FILL = '#5f81c4';

/** Uses `categories.color` from API (or passed hex); no styling if name or color missing/invalid. */
function paintExcelCategoryCell(cell, categoryName, categoryColorHex) {
  const name = String(categoryName ?? '').trim();
  if (!name) return;
  const argb = cssHexToArgb(categoryColorHex);
  if (!argb) return;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
  cell.font = { bold: true, size: 11, color: { argb: pickContrastingFontArgb(argb) } };
  cell.border = ALL_BORDERS;
}

/** Unit: fixed colors for kg / piece only; no fill if unit missing or other type. */
const EXCEL_UNIT_STYLES = {
  kg: { fill: 'FFFEF3C7', font: 'FF92400E' },
  piece: { fill: 'FFDCFCE7', font: 'FF14532D' },
};

function paintExcelUnitCell(cell, rawUnitType) {
  let u = String(rawUnitType ?? '').toLowerCase().trim().replace(/[\s-]/g, '_');
  if (!u) return;
  if (u === 'pcs' || u === 'pc' || u === 'pieces') u = 'piece';
  // Inventory UI / Redux use 'weight' for DB enum 'kg' (see inventorySlice).
  if (u === 'weight' || u === 'kilogram' || u === 'kilograms' || u === 'kgs') u = 'kg';
  const st = EXCEL_UNIT_STYLES[u];
  if (!st) return;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: st.fill } };
  cell.font = { bold: true, size: 11, color: { argb: st.font } };
  cell.border = ALL_BORDERS;
}

/** Visible cell text when DB/API omits a string (category name, unit type, …); localized via `labels.uncategorized`. */
function excelUncategorizedFallback(raw, uncategorizedLabel) {
  const trimmed = String(raw ?? '').trim();
  if (trimmed) return trimmed;
  const fb =
    uncategorizedLabel != null && String(uncategorizedLabel).trim() !== ''
      ? String(uncategorizedLabel).trim()
      : 'Uncategorized';
  return fb;
}

/** Visible category label when DB/API omits category name (localized via `labels.uncategorized`). */
function excelCategoryDisplay(rawName, uncategorizedLabel) {
  return excelUncategorizedFallback(rawName, uncategorizedLabel);
}

/** Category for a sales/refund row: offers use `labels.offerCategory`, else normal category display. */
function excelRowCategoryDisplay(row, L) {
  if (row?.is_offer) {
    const offerLabel = L?.offerCategory;
    return offerLabel != null && String(offerLabel).trim() !== ''
      ? String(offerLabel).trim()
      : 'Offer';
  }
  return excelCategoryDisplay(row?.category_name, L?.uncategorized ?? 'Uncategorized');
}

/** Unit column: localized unit via `formatUnit`, or uncategorized label when unit is missing. */
function excelUnitDisplay(rawUnit, uncategorizedLabel, formatUnitFn) {
  const rawUnitNorm =
    rawUnit != null && String(rawUnit).trim() !== '' ? String(rawUnit).trim() : '';
  if (!rawUnitNorm) return excelUncategorizedFallback('', uncategorizedLabel);
  return formatUnitFn(rawUnitNorm);
}

/**
 * Stable sort: rows whose displayed category equals `uncategorizedLabel` appear last.
 */
function sortRowsUncategorizedLast(rows, uncategorizedLabel, getRawCategoryName) {
  const u = uncategorizedLabel ?? 'Uncategorized';
  return [...(rows || [])].sort((a, b) => {
    const da = excelCategoryDisplay(getRawCategoryName(a), u);
    const db = excelCategoryDisplay(getRawCategoryName(b), u);
    const aUncat = da === u;
    const bUncat = db === u;
    if (aUncat !== bUncat) return aUncat ? 1 : -1;
    return 0;
  });
}

/** Category pill color from row (`category_color`) or `categoryColorByName` fallback map. */
function resolveExcelCategoryColorFromRow(row, categoryColorByName, uncategorizedLabel) {
  const fromRow = row.category_color ?? row.categoryColor;
  if (fromRow != null && String(fromRow).trim() !== '') return String(fromRow).trim();
  let name = String(row.category_name ?? '').trim();
  if (!name) name = excelCategoryDisplay('', uncategorizedLabel);
  if (!name || !categoryColorByName || typeof categoryColorByName !== 'object') return null;
  const hex = categoryColorByName[name];
  return hex != null && String(hex).trim() !== '' ? String(hex).trim() : null;
}

/**
 * Product-level sales grid matching the Sales page export (columns, formats, category/unit fills, totals row).
 */
function fillProductSalesExcelSheet(ws, salesData, L, { formatUnit, categoryColorByName }) {
  const uncategorized = L.uncategorized ?? 'Uncategorized';
  const rowsOrdered = sortRowsUncategorizedLast(salesData, uncategorized, (r) => (
    r?.is_offer ? (L.offerCategory ?? 'Offer') : r.category_name
  ));
  const productCodeHeader = L.productCode || L.sku;
  applyHeader(
    ws,
    [L.productName, productCodeHeader, L.category, L.unitType, L.soldQty, L.revenue, L.revenueProfit],
    [28, 16, 20, 14, 14, 16, 16],
  );

  let detailSold = 0, detailRev = 0, detailProfit = 0;
  for (const r of rowsOrdered) {
    detailSold   += parseFloat(r.sold_quantity || 0);
    detailRev    += parseFloat(r.revenue       || 0);
    detailProfit += parseFloat(r.profit        || 0);
    const catDisplay = excelRowCategoryDisplay(r, L);
    const rawUnit = r.is_offer ? 'piece' : r.unit_type;
    const rawUnitNorm =
      rawUnit != null && String(rawUnit).trim() !== '' ? String(rawUnit).trim() : '';
    const unitDisplay = excelUnitDisplay(rawUnit, uncategorized, formatUnit);
    const row = ws.addRow([
      r.product_name || '',
      r.sku          || r.product_code || '',
      catDisplay,
      unitDisplay,
      parseFloat(r.sold_quantity || 0),
      parseFloat(r.revenue       || 0),
      parseFloat(r.profit        || 0),
    ]);
    applyDataRow(row, 7);
    row.getCell(5).numFmt = '#,##0.##';
    row.getCell(6).numFmt = CURRENCY_FMT;
    row.getCell(7).numFmt = CURRENCY_FMT;
    paintExcelCategoryCell(
      row.getCell(3),
      catDisplay,
      r.is_offer
        ? OFFER_CATEGORY_FILL
        : resolveExcelCategoryColorFromRow(r, categoryColorByName, uncategorized),
    );
    paintExcelUnitCell(row.getCell(4), rawUnitNorm || undefined);
  }

  const productCount = rowsOrdered.length;
  const salesTotal = ws.addRow([
    `${L.total}: ${productCount}`,
    '',
    '',
    '',
    detailSold,
    detailRev,
    detailProfit,
  ]);
  applyTotalRow(salesTotal, 7);
  salesTotal.getCell(5).numFmt = '#,##0.##';
  salesTotal.getCell(6).numFmt = CURRENCY_FMT;
  salesTotal.getCell(7).numFmt = CURRENCY_FMT;

  ws.autoFilter = { from: 'C1', to: 'D1' };
}

// ─── Shared utilities ──────────────────────────────────────────────────────────

function toExcelDate(str) {
  if (!str) return null;
  const d = new Date(str + 'T12:00:00Z');
  if (isNaN(d.getTime())) return null;
  return (d.getTime() / 86400000) + 25569;
}

function formatDate(d) {
  if (!d) return '';
  if (typeof d === 'string') {
    const trimmed = d.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    return trimmed.slice(0, 10);
  }
  if (d instanceof Date && !Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return String(d);
}

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Sales export ──────────────────────────────────────────────────────────────

const defaultSalesLabels = {
  sheetSummary:  'Categories',
  sheetSales:    'Sales',
  category:      'Category',
  uncategorized: 'Uncategorized',
  offerCategory: 'Offer',
  soldQty:       'Sold Qty',
  revenue:       'Sales',
  revenueProfit: 'Profit',
  productName:   'Product Name',
  productCode:   'Product Code',
  sku:           'SKU',
  unitType:      'Unit Type',
  total:         'Total',
};

export async function exportSalesToExcel(salesData, filename, labels = {}) {
  const {
    formatUnitType: formatUnitTypeFn,
    categoryColorByName,
    ...labelRest
  } = labels;
  const L = { ...defaultSalesLabels, ...labelRest };
  const formatUnit =
    typeof formatUnitTypeFn === 'function'
      ? formatUnitTypeFn
      : (u) => (u == null || u === '' ? '' : String(u));

  const wb = new ExcelJS.Workbook();

  const uncategorized = L.uncategorized ?? 'Uncategorized';

  // ── Categories sheet (aggregated by category) — first tab ─────────────────
  const wsCategories = wb.addWorksheet(L.sheetSummary);
  const catTotals = {};
  for (const r of salesData || []) {
    const catKey = excelCategoryDisplay(r.category_name, uncategorized);
    if (!catTotals[catKey]) {
      catTotals[catKey] = { revenue: 0, profit: 0, category_color: null };
    }
    catTotals[catKey].revenue += parseFloat(r.revenue || 0);
    catTotals[catKey].profit  += parseFloat(r.profit || 0);
    const rowCatColor = resolveExcelCategoryColorFromRow(r, categoryColorByName, uncategorized);
    if (rowCatColor && !catTotals[catKey].category_color) {
      catTotals[catKey].category_color = rowCatColor;
    }
  }

  applyHeader(wsCategories, [L.category, L.revenue, L.revenueProfit], [28, 16, 16]);

  const categoryEntries = Object.entries(catTotals);
  const sortedCategoryEntries = [
    ...categoryEntries.filter(([k]) => k !== uncategorized),
    ...categoryEntries.filter(([k]) => k === uncategorized),
  ];

  let totalRev = 0, totalProfit = 0;
  for (const [catKey, v] of sortedCategoryEntries) {
    totalRev    += v.revenue;
    totalProfit += v.profit;
    const row = wsCategories.addRow([catKey, v.revenue, v.profit]);
    applyDataRow(row, 3);
    row.getCell(2).numFmt = CURRENCY_FMT;
    row.getCell(3).numFmt = CURRENCY_FMT;
    const summaryHex =
      v.category_color ||
      (catKey && categoryColorByName && typeof categoryColorByName === 'object'
        ? categoryColorByName[catKey]
        : null);
    paintExcelCategoryCell(row.getCell(1), catKey, summaryHex);
  }

  const categoryCount = Object.keys(catTotals).length;
  const categoriesTotalRow = wsCategories.addRow([
    `${L.total}: ${categoryCount}`,
    totalRev,
    totalProfit,
  ]);
  applyTotalRow(categoriesTotalRow, 3);
  categoriesTotalRow.getCell(2).numFmt = CURRENCY_FMT;
  categoriesTotalRow.getCell(3).numFmt = CURRENCY_FMT;

  wsCategories.autoFilter = { from: 'A1', to: 'C1' };

  const wsSales = wb.addWorksheet(L.sheetSales);
  fillProductSalesExcelSheet(wsSales, salesData, L, { formatUnit, categoryColorByName });

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

function fillOfferSalesExcelSheet(ws, salesData, L) {
  applyHeader(
    ws,
    [L.offerName, L.sku || L.productCode || 'SKU', L.bundlesSold, L.revenue, L.revenueProfit],
    [32, 16, 16, 16, 16],
  );

  let detailSold = 0, detailRev = 0, detailProfit = 0;
  for (const r of salesData || []) {
    detailSold   += parseFloat(r.sold_quantity || 0);
    detailRev    += parseFloat(r.revenue       || 0);
    detailProfit += parseFloat(r.profit        || 0);
    const row = ws.addRow([
      r.offer_name || '',
      r.sku || '',
      parseFloat(r.sold_quantity || 0),
      parseFloat(r.revenue       || 0),
      parseFloat(r.profit        || 0),
    ]);
    applyDataRow(row, 5);
    row.getCell(3).numFmt = '#,##0.##';
    row.getCell(4).numFmt = CURRENCY_FMT;
    row.getCell(5).numFmt = CURRENCY_FMT;
  }

  const offerCount = (salesData || []).length;
  const salesTotal = ws.addRow([
    `${L.total}: ${offerCount}`,
    '',
    detailSold,
    detailRev,
    detailProfit,
  ]);
  applyTotalRow(salesTotal, 5);
  salesTotal.getCell(3).numFmt = '#,##0.##';
  salesTotal.getCell(4).numFmt = CURRENCY_FMT;
  salesTotal.getCell(5).numFmt = CURRENCY_FMT;

  ws.autoFilter = { from: 'A1', to: 'B1' };
}

const defaultOfferSalesLabels = {
  sheetOfferSales: 'Offer Sales',
  offerName:       'Offer Name',
  sku:             'Offer SKU',
  bundlesSold:     'Bundles Sold',
  revenue:         'Sales',
  revenueProfit:   'Revenue',
  total:           'Total',
};

export async function exportOfferSalesToExcel(salesData, filename, labels = {}) {
  const L = { ...defaultOfferSalesLabels, ...labels };
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(L.sheetOfferSales);
  fillOfferSalesExcelSheet(ws, salesData, L);
  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

// ─── Sessions export ───────────────────────────────────────────────────────────

/** Excel fills / font colors aligned with admin status semantics (ARGB). */
const SESSION_STATUS_STYLE = {
  open:         { fill: 'FFEEF2FF', font: 'FF4338CA' },
  closed:       { fill: 'FFF1F5F9', font: 'FF475569' },
  force_closed: { fill: 'FFFEF3C7', font: 'FFB45309' },
  reviewed:     { fill: 'FFDCFCE7', font: 'FF166534' },
};
const SESSION_STATUS_FALLBACK = { fill: 'FFF1F5F9', font: 'FF475569' };

function paintSessionStatusCell(cell, rawStatus) {
  const st = SESSION_STATUS_STYLE[rawStatus] || SESSION_STATUS_FALLBACK;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: st.fill } };
  cell.font = { bold: true, size: 11, color: { argb: st.font } };
}

function paintCashDiffCell(cell, value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  const v = Number.isFinite(n) ? n : 0;
  let fill;
  let fgArgb;
  if (v > 0.005) {
    fill = 'FFDCFCE7';
    fgArgb = 'FF15803D';
  } else if (v < -0.005) {
    fill = 'FFFEE2E2';
    fgArgb = 'FFB91C1C';
  } else {
    fill = 'FFF8FAFC';
    fgArgb = 'FF64748B';
  }
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  cell.font = { bold: true, size: 11, color: { argb: fgArgb } };
}

const defaultSessionLabels = {
  sheetSessions:    'Sessions',
  cashier:          'Cashier',
  register:         'Register',
  startTime:        'Start Time',
  endTime:          'End Time',
  status:           'Status',
  openingCash:      'Opening Cash',
  expectedCash:     'Expected Cash',
  expectedCard:     'Expected Card',
  expectedInstapay: 'Expected InstaPay',
  expectedWallet:   'Expected Wallet',
  refundAmount:     'Refund Amount',
  receivedCash:     'Received Cash',
  cashDiff:         'Cash diff',
  closedBy:         'Closed By',
  reviewedBy:      'Reviewed By',
  total:            'Total',
};

export async function exportSessionsToExcel(sessions, filename, labels = {}) {
  const merged = { ...defaultSessionLabels, ...labels };
  const { formatSessionStatus: formatSessionStatusFn, ...L } = merged;
  const formatSessionStatus =
    typeof formatSessionStatusFn === 'function'
      ? formatSessionStatusFn
      : (st) => (st == null ? '' : String(st));

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(L.sheetSessions);

  const headers   = [
    L.cashier, L.register, L.startTime, L.endTime, L.status,
    L.openingCash, L.expectedCash, L.expectedCard, L.expectedInstapay, L.expectedWallet,
    L.refundAmount, L.receivedCash, L.cashDiff, L.closedBy, L.reviewedBy,
  ];
  const colWidths = [20, 18, 22, 22, 14, 16, 16, 16, 16, 16, 16, 16, 16, 18, 18];
  applyHeader(ws, headers, colWidths);

  const list = sessions || [];
  let totalNet = 0;
  for (const s of list) {
    const net =
      (parseFloat(s.expected_closing_cash      || 0) - parseFloat(s.opening_cash || 0)) +
      parseFloat(s.expected_closing_card       || 0) +
      parseFloat(s.expected_closing_instapay   || 0) +
      parseFloat(s.expected_closing_wallet     || 0);
    totalNet += net;
    const row = ws.addRow([
      s.cashier_name   || '',
      s.register_name  || '',
      s.start_time ? new Date(s.start_time).toLocaleString() : '',
      s.end_time   ? new Date(s.end_time).toLocaleString()   : '',
      formatSessionStatus(s.status),
      parseFloat(s.opening_cash                || 0),
      parseFloat(s.expected_closing_cash       || 0),
      parseFloat(s.expected_closing_card       || 0),
      parseFloat(s.expected_closing_instapay   || 0),
      parseFloat(s.expected_closing_wallet     || 0),
      parseFloat(s.refund_amount               || 0),
      parseFloat(s.closing_cash                ?? 0),
      net,
      s.closed_by_name || '',
      s.reviewed_by_name || '',
    ]);
    applyDataRow(row, headers.length);
    [6, 7, 8, 9, 10, 11, 12, 13].forEach((c) => { row.getCell(c).numFmt = CURRENCY_FMT; });
    paintSessionStatusCell(row.getCell(5), s.status);
    paintCashDiffCell(row.getCell(13), net);
  }

  const sumOpening = list.reduce((acc, x) => acc + parseFloat(x.opening_cash || 0), 0);
  const sumReceived = list.reduce((acc, x) => acc + parseFloat(x.closing_cash || 0), 0);
  const sumRefund = list.reduce((acc, x) => acc + parseFloat(x.refund_amount || 0), 0);
  const totalRow = ws.addRow([
    `${L.total}: ${list.length}`,
    '', '', '', '',
    sumOpening,
    '',
    '', '', '',
    sumRefund,
    sumReceived,
    totalNet,
    '', '',
  ]);
  applyTotalRow(totalRow, headers.length);
  [6, 11, 12, 13].forEach((c) => { totalRow.getCell(c).numFmt = CURRENCY_FMT; });
  paintCashDiffCell(totalRow.getCell(13), totalNet);

  ws.autoFilter = { from: 'A1', to: 'O1' };

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

// ─── Inventory export ──────────────────────────────────────────────────────────

const defaultInventoryLabels = {
  sheetInventory: 'Inventory',
  productName:    'Product Name',
  sku:            'SKU',
  category:       'Category',
  uncategorized:  'Uncategorized',
  unitType:       'Unit Type',
  costPrice:      'Cost Price',
  sellingPrice:   'Selling Price',
  stock:          'Stock',
  total:          'Total',
};

function fillInventoryExcelSheet(ws, items, L, { formatUnit, categoryColorByName }) {
  const uncategorized = L.uncategorized ?? 'Uncategorized';
  const itemsOrdered = sortRowsUncategorizedLast(items, uncategorized, (item) => item.category_name);
  applyHeader(
    ws,
    [L.productName, L.sku, L.category, L.unitType, L.costPrice, L.sellingPrice, L.stock],
    [28, 16, 20, 14, 16, 16, 12],
  );

  for (const item of itemsOrdered) {
    const catStr = excelCategoryDisplay(item.category_name, uncategorized);
    const unitRaw = item.unit_type;
    const unitNorm = unitRaw != null && String(unitRaw).trim() !== '' ? String(unitRaw).trim() : '';
    const unitCell = excelUnitDisplay(unitRaw, uncategorized, formatUnit);

    const row = ws.addRow([
      item.name || item.product_name || '',
      item.sku || '',
      catStr,
      unitCell,
      parseFloat(item.cost_price || 0),
      parseFloat(item.selling_price || 0),
      parseFloat(item.stock_quantity || 0),
    ]);
    applyDataRow(row, 7);
    row.getCell(5).numFmt = CURRENCY_FMT;
    row.getCell(6).numFmt = CURRENCY_FMT;
    row.getCell(7).numFmt = '#,##0.##';
    paintExcelCategoryCell(
      row.getCell(3),
      catStr,
      resolveExcelCategoryColorFromRow(item, categoryColorByName, uncategorized),
    );
    paintExcelUnitCell(row.getCell(4), unitNorm || undefined);
  }

  const itemCount = itemsOrdered.length;
  const totalRow = ws.addRow([`${L.total}: ${itemCount}`, '', '', '', '', '', '']);
  applyTotalRow(totalRow, 7);

  ws.autoFilter = { from: 'C1', to: 'D1' };
}

export async function exportInventoryToExcel(items, filename, labels = {}) {
  const merged = { ...defaultInventoryLabels, ...labels };
  const { formatUnitType: formatUnitTypeFn, categoryColorByName, ...L } = merged;
  const formatUnit =
    typeof formatUnitTypeFn === 'function' ? formatUnitTypeFn : (u) => (u == null ? '' : String(u));

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(L.sheetInventory);
  fillInventoryExcelSheet(ws, items, L, { formatUnit, categoryColorByName });

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

// ─── Orders (Reports) export ───────────────────────────────────────────────────

function isDeliveryOrder(o) {
  if (!o) return false;
  const ot = String(o.order_type || '')
    .toLowerCase()
    .replace(/[\s-]/g, '_');
  if (ot === 'delivery') return true;
  if (ot === 'in_store' || ot === 'instore') return false;
  const reg = o.delivery_region;
  return reg != null && String(reg).trim() !== '';
}

const ORDER_PAYMENT_STYLE = {
  cash:     { fill: 'FFDCFCE7', font: 'FF15803D' },
  card:     { fill: 'FFEEF2FF', font: 'FF4338CA' },
  instapay: { fill: 'FFEDE9FE', font: 'FF6D28D9' },
  wallet:   { fill: 'FFCCFBF1', font: 'FF0F766E' },
  mixed:    { fill: 'FFFEF3C7', font: 'FFB45309' },
};
const ORDER_PAYMENT_FALLBACK = { fill: 'FFF1F5F9', font: 'FF475569' };

function paintOrderPaymentCell(cell, rawMethod) {
  const key = String(rawMethod || '').toLowerCase().trim();
  const st = ORDER_PAYMENT_STYLE[key] || ORDER_PAYMENT_FALLBACK;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: st.fill } };
  cell.font = { bold: true, size: 11, color: { argb: st.font } };
}

const ORDER_STATUS_STYLE = {
  completed:          { fill: 'FFDCFCE7', font: 'FF15803D' },
  refunded:           { fill: 'FFFEE2E2', font: 'FFB91C1C' },
  partially_refunded: { fill: 'FFFEF3C7', font: 'FFB45309' },
};
const ORDER_STATUS_FALLBACK = { fill: 'FFF1F5F9', font: 'FF475569' };

function paintOrderStatusCell(cell, rawStatus) {
  const key = String(rawStatus || '').toLowerCase().trim();
  const st = ORDER_STATUS_STYLE[key] || ORDER_STATUS_FALLBACK;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: st.fill } };
  cell.font = { bold: true, size: 11, color: { argb: st.font } };
}

function formatDiscountApprover(o) {
  const name = o.discount_approver_name;
  if (!name) return '';
  const role = o.discount_approver_role;
  return role ? `${name} (${role})` : String(name);
}

function writeOrdersSheet(
  wb,
  sheetTitle,
  orders,
  L,
  { includeCustomer, formatPaymentMethod, formatOrderStatus },
) {
  const ws = wb.addWorksheet(sheetTitle);

  if (includeCustomer) {
    applyHeader(
      ws,
      [
        L.receiptNo,
        L.date,
        L.customer,
        L.deliveryRegion,
        L.deliveryPerson,
        L.cashier,
        L.paymentMethod,
        L.promoName,
        L.promoPercentage,
        L.discountApprover,
        L.discount,
        L.tax,
        L.total,
        L.status,
        L.discountNotes,
      ],
      [18, 22, 22, 26, 20, 20, 18, 20, 12, 22, 14, 14, 16, 16, 28],
    );

    let grandTotal = 0;
    for (const o of orders || []) {
      const amt = parseFloat(o.total_amount || 0);
      grandTotal += amt;
      const disc = parseFloat(o.discount_percentage || 0) / 100;
      const tax = parseFloat(o.tax_percentage || 0) / 100;
      const promoPctRaw = parseFloat(o.promo_percentage || 0);
      const row = ws.addRow([
        o.receipt_num || `#${o.id}`,
        o.created_at ? new Date(o.created_at).toLocaleString() : '',
        o.customer_name || '',
        o.delivery_region != null ? String(o.delivery_region) : '',
        o.delivery_person_name != null ? String(o.delivery_person_name) : '',
        o.cashier_name || '',
        formatPaymentMethod(o.payment_method),
        o.promo_name || '',
        promoPctRaw > 0 ? promoPctRaw / 100 : '',
        formatDiscountApprover(o),
        disc,
        tax,
        amt,
        formatOrderStatus(o.status),
        o.discount_notes || '',
      ]);
      applyDataRow(row, 15);
      if (promoPctRaw > 0) row.getCell(9).numFmt = PCT_FMT;
      row.getCell(11).numFmt = PCT_FMT;
      row.getCell(12).numFmt = PCT_FMT;
      row.getCell(13).numFmt = CURRENCY_FMT;
      paintOrderPaymentCell(row.getCell(7), o.payment_method);
      paintOrderStatusCell(row.getCell(14), o.status);
    }

    const totalRow = ws.addRow([
      L.grandTotal,
      '', '', '', '', '', '', '', '', '', '', '',
      grandTotal,
      '', '',
    ]);
    applyTotalRow(totalRow, 15);
    totalRow.getCell(13).numFmt = CURRENCY_FMT;
    ws.autoFilter = { from: 'C1', to: 'N1' };
  } else {
    applyHeader(
      ws,
      [
        L.receiptNo,
        L.date,
        L.cashier,
        L.paymentMethod,
        L.promoName,
        L.promoPercentage,
        L.discountApprover,
        L.discount,
        L.tax,
        L.total,
        L.status,
        L.discountNotes,
      ],
      [18, 22, 20, 18, 20, 12, 22, 14, 14, 16, 16, 28],
    );

    let grandTotal = 0;
    for (const o of orders || []) {
      const amt = parseFloat(o.total_amount || 0);
      grandTotal += amt;
      const disc = parseFloat(o.discount_percentage || 0) / 100;
      const tax = parseFloat(o.tax_percentage || 0) / 100;
      const promoPctRaw = parseFloat(o.promo_percentage || 0);
      const row = ws.addRow([
        o.receipt_num || `#${o.id}`,
        o.created_at ? new Date(o.created_at).toLocaleString() : '',
        o.cashier_name || '',
        formatPaymentMethod(o.payment_method),
        o.promo_name || '',
        promoPctRaw > 0 ? promoPctRaw / 100 : '',
        formatDiscountApprover(o),
        disc,
        tax,
        amt,
        formatOrderStatus(o.status),
        o.discount_notes || '',
      ]);
      applyDataRow(row, 12);
      if (promoPctRaw > 0) row.getCell(6).numFmt = PCT_FMT;
      row.getCell(8).numFmt = PCT_FMT;
      row.getCell(9).numFmt = PCT_FMT;
      row.getCell(10).numFmt = CURRENCY_FMT;
      paintOrderPaymentCell(row.getCell(4), o.payment_method);
      paintOrderStatusCell(row.getCell(11), o.status);
    }

    const totalRow = ws.addRow([
      L.grandTotal,
      '', '', '', '', '', '', '', '',
      grandTotal,
      '', '',
    ]);
    applyTotalRow(totalRow, 12);
    totalRow.getCell(10).numFmt = CURRENCY_FMT;
    ws.autoFilter = { from: 'C1', to: 'K1' };
  }
}

const defaultOrdersLabels = {
  sheetOrdersInStore:  'In-Store Orders',
  sheetOrdersDelivery: 'Delivery Orders',
  receiptNo:       'Receipt No.',
  date:            'Date',
  customer:        'Customer',
  deliveryRegion:  'Delivery Region',
  deliveryPerson:  'Delivery Person',
  cashier:        'Cashier',
  paymentMethod:  'Payment Method',
  promoName:       'Promo Name',
  promoPercentage: 'Promo %',
  discountApprover: 'Discount Approver',
  discount:        'Discount',
  tax:             'Tax',
  total:           'Total',
  status:          'Status',
  discountNotes:   'Discount Notes',
  grandTotal:      'Grand Total',
};

export async function exportOrdersToExcel(orders, filename, labels = {}) {
  const {
    formatPaymentMethod: formatPaymentMethodFn,
    formatOrderStatus: formatOrderStatusFn,
    ...labelRest
  } = labels;
  const L = { ...defaultOrdersLabels, ...labelRest };
  const formatPaymentMethod =
    typeof formatPaymentMethodFn === 'function'
      ? formatPaymentMethodFn
      : (m) => (m == null ? '' : String(m));
  const formatOrderStatus =
    typeof formatOrderStatusFn === 'function'
      ? formatOrderStatusFn
      : (s) => (s == null ? '' : String(s));

  const list = orders || [];
  const deliveryOrders = list.filter(isDeliveryOrder);
  const inStoreOrders  = list.filter((o) => !isDeliveryOrder(o));

  const sheetOpts = { formatPaymentMethod, formatOrderStatus };

  const wb = new ExcelJS.Workbook();
  writeOrdersSheet(wb, L.sheetOrdersInStore, inStoreOrders, L, {
    ...sheetOpts,
    includeCustomer: false,
  });
  writeOrdersSheet(wb, L.sheetOrdersDelivery, deliveryOrders, L, {
    ...sheetOpts,
    includeCustomer: true,
  });

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

// ─── Refunds export ────────────────────────────────────────────────────────────

const defaultRefundsLabels = {
  sheetRefunds:  'Refunds',
  receiptNo:     'Receipt No.',
  date:          'Date',
  orderCashier:  'Order cashier',
  refundCashier: 'Refunded by',
  cashier:       'Cashier',
  approvedBy:    'Approved By',
  reason:        'Reason',
  amount:        'Amount',
  status:        'Status',
  totalRefunded: 'Total Refunded',
};

const REFUND_ORDER_STATUS_STYLE = {
  refunded:           { fill: 'FFFEE2E2', font: 'FFB91C1C' },
  partially_refunded: { fill: 'FFFEF3C7', font: 'FFB45309' },
  rejected:           { fill: 'FFFEE2E2', font: 'FFB91C1C' },
  pending:            { fill: 'FFFEF3C7', font: 'FFB45309' },
  completed:          { fill: 'FFDCFCE7', font: 'FF15803D' },
};
const REFUND_ORDER_STATUS_FALLBACK = { fill: 'FFF1F5F9', font: 'FF475569' };

function paintRefundOrderStatusCell(cell, rawStatus) {
  const key = String(rawStatus || '').toLowerCase().trim();
  const st = REFUND_ORDER_STATUS_STYLE[key] || REFUND_ORDER_STATUS_FALLBACK;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: st.fill } };
  cell.font = { bold: true, size: 11, color: { argb: st.font } };
}

export async function exportRefundsToExcel(refunds, filename, labels = {}) {
  const {
    formatRefundOrderStatus: formatRefundOrderStatusFn,
    ...labelRest
  } = labels;
  const L = { ...defaultRefundsLabels, ...labelRest };
  const formatRefundOrderStatus =
    typeof formatRefundOrderStatusFn === 'function'
      ? formatRefundOrderStatusFn
      : (s) => (s == null ? '' : String(s));

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(L.sheetRefunds);

  applyHeader(
    ws,
    [L.receiptNo, L.date, L.orderCashier, L.refundCashier, L.approvedBy, L.reason, L.amount, L.status],
    [18, 22, 20, 20, 20, 28, 16, 16],
  );

  let total = 0;
  for (const r of refunds || []) {
    const amt = parseFloat(r.amount || 0);
    total += amt;
    const orderSt = r.order_status || r.status;
    const row = ws.addRow([
      r.receipt_num  || '',
      r.created_at ? new Date(r.created_at).toLocaleString() : '',
      r.order_cashier_name || '',
      r.refund_cashier_name || r.cashier_name || '',
      r.approver_name || r.approved_by_name || '',
      r.reason        || '',
      amt,
      formatRefundOrderStatus(orderSt),
    ]);
    applyDataRow(row, 8);
    row.getCell(7).numFmt = CURRENCY_FMT;
    paintRefundOrderStatusCell(row.getCell(8), orderSt);
  }

  const totalRow = ws.addRow([L.totalRefunded, '', '', '', '', '', total, '']);
  applyTotalRow(totalRow, 8);
  totalRow.getCell(7).numFmt = CURRENCY_FMT;

  ws.autoFilter = { from: 'C1', to: 'H1' };

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

// ─── Expenses export ───────────────────────────────────────────────────────────

const defaultExpensesLabels = {
  sheetCategories: 'Categories',
  sheetExpenses: 'Expenses',
  name:          'Name',
  category:      'Category',
  uncategorized: 'Uncategorized',
  date:          'Date',
  amount:        'Amount',
  total:         'Total',
};

/** Aggregated expenses by category (first sheet); uncategorized row last. */
function fillExpensesCategoriesExcelSheet(ws, expenses, L, { categoryColorByName } = {}) {
  const uncategorized = L.uncategorized ?? 'Uncategorized';
  const catTotals = {};
  for (const e of expenses || []) {
    const catKey = excelCategoryDisplay(e.category_name, uncategorized);
    if (!catTotals[catKey]) {
      catTotals[catKey] = { amount: 0, category_color: null };
    }
    catTotals[catKey].amount += parseFloat(e.amount || 0);
    const hex = resolveExcelCategoryColorFromRow(e, categoryColorByName, uncategorized);
    if (hex && !catTotals[catKey].category_color) {
      catTotals[catKey].category_color = hex;
    }
  }

  applyHeader(ws, [L.category, L.amount], [28, 16]);

  const categoryEntries = Object.entries(catTotals);
  const sortedCategoryEntries = [
    ...categoryEntries.filter(([k]) => k !== uncategorized),
    ...categoryEntries.filter(([k]) => k === uncategorized),
  ];

  let totalAmt = 0;
  for (const [catKey, v] of sortedCategoryEntries) {
    totalAmt += v.amount;
    const row = ws.addRow([catKey, v.amount]);
    applyDataRow(row, 2);
    row.getCell(2).numFmt = CURRENCY_FMT;
    const summaryHex =
      v.category_color ||
      (catKey && categoryColorByName && typeof categoryColorByName === 'object'
        ? categoryColorByName[catKey]
        : null);
    paintExcelCategoryCell(row.getCell(1), catKey, summaryHex);
  }

  const categoryCount = Object.keys(catTotals).length;
  const categoriesTotalRow = ws.addRow([`${L.total}: ${categoryCount}`, totalAmt]);
  applyTotalRow(categoriesTotalRow, 2);
  categoriesTotalRow.getCell(2).numFmt = CURRENCY_FMT;

  ws.autoFilter = { from: 'A1', to: 'B1' };
}

function fillExpensesExcelSheet(ws, expenses, L) {
  const uncategorized = L.uncategorized ?? 'Uncategorized';
  const expensesOrdered = sortRowsUncategorizedLast(expenses, uncategorized, (e) => e.category_name);
  applyHeader(ws, [L.name, L.category, L.date, L.amount], [28, 22, 16, 16]);

  let detailTotal = 0;
  for (const e of expensesOrdered) {
    const amt = parseFloat(e.amount || 0);
    detailTotal += amt;
    const dateStr = formatDate(e.bought_at || e.date);
    const dateVal = toExcelDate(dateStr) ?? dateStr;
    const catStr = excelCategoryDisplay(e.category_name, uncategorized);
    const row = ws.addRow([e.name || '', catStr, dateVal, amt]);
    applyDataRow(row, 4);
    if (typeof dateVal === 'number') row.getCell(3).numFmt = DATE_FMT;
    row.getCell(4).numFmt = CURRENCY_FMT;
  }

  const expenseCount = expensesOrdered.length;
  const detailTotalRow = ws.addRow([`${L.total}: ${expenseCount}`, '', '', detailTotal]);
  applyTotalRow(detailTotalRow, 4);
  detailTotalRow.getCell(4).numFmt = CURRENCY_FMT;
  ws.autoFilter = { from: 'B1', to: 'C1' };
}

export async function exportExpensesToExcel(expenses, filename, labels = {}) {
  const { categoryColorByName, ...labelRest } = labels;
  const L = { ...defaultExpensesLabels, ...labelRest };
  const wb = new ExcelJS.Workbook();
  const wsCategories = wb.addWorksheet(L.sheetCategories);
  fillExpensesCategoriesExcelSheet(wsCategories, expenses, L, { categoryColorByName });
  const ws = wb.addWorksheet(L.sheetExpenses);
  fillExpensesExcelSheet(ws, expenses, L);

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

// ─── Purchases export ──────────────────────────────────────────────────────────

const defaultPurchasesLabels = {
  sheetCategories:      'Categories',
  sheetPurchases:       'Purchases',
  totalPurchasesByCategory: 'Total Purchases',
  productName:       'Product Name',
  category:          'Category',
  uncategorized:     'Uncategorized',
  unitType:          'Unit Type',
  supplier:          'Supplier',
  date:              'Date',
  quantity:          'Quantity',
  unitPrice:         'Unit price',
  unitSellingPrice:  'Unit selling price',
  oldCostPrice:      'Old cost price',
  oldSellingPrice:   'Old selling price',
  inventoryBefore:   'Inventory before',
  inventoryAfter:    'Inventory after',
  totalPrice:        'Total Price',
  total:             'Total',
};

/** Aggregated purchases by category (first sheet); uncategorized row last. */
function fillPurchasesCategoriesExcelSheet(ws, purchases, L, { categoryColorByName } = {}) {
  const uncategorized = L.uncategorized ?? 'Uncategorized';
  const catTotals = {};
  for (const p of purchases || []) {
    const catKey = excelCategoryDisplay(p.category_name, uncategorized);
    if (!catTotals[catKey]) {
      catTotals[catKey] = { totalPrice: 0, category_color: null };
    }
    catTotals[catKey].totalPrice += parseFloat(p.total_price || 0);
    const hex = resolveExcelCategoryColorFromRow(p, categoryColorByName, uncategorized);
    if (hex && !catTotals[catKey].category_color) {
      catTotals[catKey].category_color = hex;
    }
  }

  const totalPurchasesHeader = L.totalPurchasesByCategory ?? 'Total Purchases';
  applyHeader(ws, [L.category, totalPurchasesHeader], [28, 16]);

  const categoryEntries = Object.entries(catTotals);
  const sortedCategoryEntries = [
    ...categoryEntries.filter(([k]) => k !== uncategorized),
    ...categoryEntries.filter(([k]) => k === uncategorized),
  ];

  let grandSum = 0;
  for (const [catKey, v] of sortedCategoryEntries) {
    grandSum += v.totalPrice;
    const row = ws.addRow([catKey, v.totalPrice]);
    applyDataRow(row, 2);
    row.getCell(2).numFmt = CURRENCY_FMT;
    const summaryHex =
      v.category_color ||
      (catKey && categoryColorByName && typeof categoryColorByName === 'object'
        ? categoryColorByName[catKey]
        : null);
    paintExcelCategoryCell(row.getCell(1), catKey, summaryHex);
  }

  const categoryCount = Object.keys(catTotals).length;
  const categoriesTotalRow = ws.addRow([`${L.total}: ${categoryCount}`, grandSum]);
  applyTotalRow(categoriesTotalRow, 2);
  categoriesTotalRow.getCell(2).numFmt = CURRENCY_FMT;

  ws.autoFilter = { from: 'A1', to: 'B1' };
}

function fillPurchasesExcelSheet(ws, purchases, L, { formatUnit, categoryColorByName }) {
  const uncategorized = L.uncategorized ?? 'Uncategorized';
  const purchasesOrdered = sortRowsUncategorizedLast(purchases, uncategorized, (p) => p.category_name);
  applyHeader(
    ws,
    [
      L.productName,
      L.category,
      L.unitType,
      L.supplier,
      L.date,
      L.quantity,
      L.unitPrice,
      L.unitSellingPrice,
      L.oldCostPrice,
      L.oldSellingPrice,
      L.inventoryBefore,
      L.inventoryAfter,
      L.totalPrice,
    ],
    [28, 18, 14, 22, 14, 12, 14, 14, 14, 14, 14, 14, 16],
  );

  let grandTotal = 0;
  for (const p of purchasesOrdered) {
    const total = parseFloat(p.total_price || 0);
    grandTotal += total;
    const dateStr = formatDate(p.purchased_at);
    const dateVal = toExcelDate(dateStr) ?? dateStr;
    const qty = parseFloat(p.quantity || 0);
    const unitRaw = p.product_unit_type ?? p.unit_type;
    const unitNorm = unitRaw != null && String(unitRaw).trim() !== '' ? String(unitRaw).trim() : '';
    const unitCell = excelUnitDisplay(unitRaw, uncategorized, formatUnit);
    const catStr = excelCategoryDisplay(p.category_name, uncategorized);
    const beforeRaw = p.old_inventory;
    const beforeNum =
      beforeRaw != null && beforeRaw !== '' ? parseFloat(beforeRaw) : null;
    const invAfter =
      beforeNum != null && Number.isFinite(beforeNum) ? beforeNum + qty : qty;
    const oldCost =
      p.old_cost_price != null && p.old_cost_price !== '' ? parseFloat(p.old_cost_price) : null;
    const oldSell =
      p.old_selling_price != null && p.old_selling_price !== '' ? parseFloat(p.old_selling_price) : null;

    const row = ws.addRow([
      p.product_name || '',
      catStr,
      unitCell,
      p.supplier_name || '',
      dateVal,
      qty,
      parseFloat(p.unit_price || 0),
      parseFloat(p.unit_selling_price || 0),
      oldCost != null && Number.isFinite(oldCost) ? oldCost : null,
      oldSell != null && Number.isFinite(oldSell) ? oldSell : null,
      beforeNum != null && Number.isFinite(beforeNum) ? beforeNum : null,
      invAfter,
      total,
    ]);
    applyDataRow(row, 13);
    if (typeof dateVal === 'number') row.getCell(5).numFmt = DATE_FMT;
    row.getCell(6).numFmt = '#,##0.###';
    [7, 8, 9, 10].forEach((c) => {
      row.getCell(c).numFmt = CURRENCY_FMT;
    });
    row.getCell(11).numFmt = '#,##0.###';
    row.getCell(12).numFmt = '#,##0.###';
    row.getCell(13).numFmt = CURRENCY_FMT;
    paintExcelCategoryCell(
      row.getCell(2),
      catStr,
      resolveExcelCategoryColorFromRow(p, categoryColorByName, uncategorized),
    );
    paintExcelUnitCell(row.getCell(3), unitNorm || undefined);
  }

  const purchaseCount = purchasesOrdered.length;
  const totalRow = ws.addRow([
    `${L.total}: ${purchaseCount}`,
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    grandTotal,
  ]);
  applyTotalRow(totalRow, 13);
  totalRow.getCell(13).numFmt = CURRENCY_FMT;

  ws.autoFilter = { from: 'B1', to: 'E1' };
}

export async function exportPurchasesToExcel(purchases, filename, labels = {}) {
  const { formatUnitType: formatUnitTypeFn, categoryColorByName, ...labelRest } = labels;
  const L = { ...defaultPurchasesLabels, ...labelRest };
  const formatUnit =
    typeof formatUnitTypeFn === 'function' ? formatUnitTypeFn : (u) => (u == null ? '' : String(u));

  const wb = new ExcelJS.Workbook();
  const wsCategories = wb.addWorksheet(L.sheetCategories);
  fillPurchasesCategoriesExcelSheet(wsCategories, purchases, L, { categoryColorByName });
  const ws = wb.addWorksheet(L.sheetPurchases);
  fillPurchasesExcelSheet(ws, purchases, L, { formatUnit, categoryColorByName });

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

// ─── Monthly Closing export ────────────────────────────────────────────────────

const defaultMonthlyLabels = {
  sheetSummary:           'Summary',
  sheetSales:             'Sales',
  sheetPurchases:         'Purchases',
  sheetExpenses:          'Expenses',
  sheetRefunds:           'Refunds',
  sheetInventory:         'Inventory',
  startDate:          'Start Date',
  endDate:            'End Date',
  closingDate:        'Closing Date',
  totalExpenses:      'Total Expenses',
  totalSales:         'Total Sales',
  totalPurchases:     'Total Purchases',
  totalRefunds:       'Total Refunds',
  totalPurchasesByCategory: 'Total Purchases',
  totalRevenueProfit: 'Total Revenue',
  productName:        'Product Name',
  sku:                'SKU',
  category:           'Category',
  uncategorized:      'Uncategorized',
  offerCategory:      'Offer',
  unitType:           'Unit Type',
  soldQty:            'Sold Qty',
  revenue:            'Sales',
  revenueProfit:      'Profit',
  productCode:        'Product Code',
  supplier:           'Supplier',
  quantity:           'Quantity',
  unitPrice:          'Unit Price',
  unitSellingPrice:   'Unit selling price',
  oldCostPrice:       'Old cost price',
  oldSellingPrice:    'Old selling price',
  inventoryBefore:   'Inventory before',
  inventoryAfter:    'Inventory after',
  totalPrice:         'Total Price',
  date:               'Date',
  name:               'Name',
  code:               'Code',
  amount:             'Amount',
  costPrice:          'Cost Price',
  sellingPrice:       'Selling Price',
  stock:              'Stock',
  total:              'Total',
  refundedQty:        'Refunded qty',
  refundedAmount:     'Refunded amount',
  refundProductCategory: 'Product category',
};

/** Monthly closing — refund line items by product (filters on category column). */
function fillMonthlyClosingRefundsExcelSheet(ws, refunds, L, { categoryColorByName } = {}) {
  const uncategorized = L.uncategorized ?? 'Uncategorized';
  applyHeader(
    ws,
    [L.name ?? L.productName, L.code ?? L.productCode ?? L.sku, L.category, L.refundedQty, L.refundedAmount],
    [28, 16, 20, 14, 16],
  );

  const rowsOrdered = sortRowsUncategorizedLast(refunds, uncategorized, (r) => r.category_name);

  let sumQty = 0;
  let sumAmt = 0;
  for (const r of rowsOrdered) {
    const qty = parseFloat(r.refunded_quantity || 0);
    const amt = parseFloat(r.refunded_amount || 0);
    sumQty += qty;
    sumAmt += amt;
    const catDisplay = excelCategoryDisplay(r.category_name, uncategorized);
    const row = ws.addRow([
      r.product_name || '',
      r.sku || '',
      catDisplay,
      qty,
      amt,
    ]);
    applyDataRow(row, 5);
    row.getCell(4).numFmt = '#,##0.###';
    row.getCell(5).numFmt = CURRENCY_FMT;
    paintExcelCategoryCell(
      row.getCell(3),
      catDisplay,
      resolveExcelCategoryColorFromRow(r, categoryColorByName, uncategorized),
    );
  }

  const lineCount = rowsOrdered.length;
  const totalRow = ws.addRow([
    `${L.total}: ${lineCount}`,
    '',
    '',
    sumQty,
    sumAmt,
  ]);
  applyTotalRow(totalRow, 5);
  totalRow.getCell(4).numFmt = '#,##0.###';
  totalRow.getCell(5).numFmt = CURRENCY_FMT;

  ws.autoFilter = { from: 'C1', to: 'C1' };
}

export async function exportMonthlyClosingToExcel(data, filename, labels = {}) {
  const { formatUnitType: formatUnitTypeFn, categoryColorByName, ...labelRest } = labels;
  const L = { ...defaultMonthlyLabels, ...labelRest };
  const formatUnit =
    typeof formatUnitTypeFn === 'function' ? formatUnitTypeFn : (u) => (u == null ? '' : u);
  const wb = new ExcelJS.Workbook();
  const { summary, sales, purchases, expenses, inventory, refunds } = data;

  // ── Summary sheet (key-value layout) ─────────────────────────────────────
  const wsSummary = wb.addWorksheet(L.sheetSummary);
  wsSummary.columns = [{ width: 34 }, { width: 22 }];
  wsSummary.views   = [{ state: 'normal' }];

  const startStr   = formatDate(summary?.start_date);
  const closingStr = formatDate(summary?.closing_date);
  const endLabel = L.endDate != null && String(L.endDate).trim() !== '' ? L.endDate : L.closingDate;

  const kvRows = [
    [
      L.startDate,
      toExcelDate(startStr) ?? startStr,
      { labelFill: MC_SUM_L_START, valueFill: MC_SUM_V_START, numFmt: DATE_FMT },
    ],
    [
      endLabel,
      toExcelDate(closingStr) ?? closingStr,
      { labelFill: MC_SUM_L_END, valueFill: MC_SUM_V_END, numFmt: DATE_FMT },
    ],
    [
      L.totalExpenses,
      parseFloat(summary?.total_expenses ?? 0),
      { labelFill: MC_SUM_L_EXP, valueFill: MC_SUM_V_EXP, numFmt: CURRENCY_FMT },
    ],
    [
      L.totalPurchases,
      parseFloat(summary?.total_purchases ?? 0),
      { labelFill: MC_SUM_L_PUR, valueFill: MC_SUM_V_PUR, numFmt: CURRENCY_FMT },
    ],
    [
      L.totalRefunds,
      parseFloat(summary?.total_refunds ?? 0),
      { labelFill: MC_SUM_L_REF, valueFill: MC_SUM_V_REF, numFmt: CURRENCY_FMT },
    ],
    [
      L.totalSales,
      parseFloat(summary?.total_sales ?? 0),
      { labelFill: MC_SUM_L_SAL, valueFill: MC_SUM_V_SAL, numFmt: CURRENCY_FMT },
    ],
    [
      L.totalRevenueProfit,
      parseFloat(summary?.total_sales_revenue ?? 0),
      {
        labelFill: MC_SUM_L_REV,
        valueFill: MC_SUM_V_REV,
        numFmt: CURRENCY_FMT,
        valueBold: true,
      },
    ],
  ];

  kvRows.forEach(([label, value, opts], i) => {
    applyKVRow(wsSummary, label, value, i + 1, opts);
  });

  const expensesSheetLabels = {
    ...defaultExpensesLabels,
    sheetExpenses: L.sheetExpenses,
    name: L.name,
    category: L.category,
    date: L.date,
    amount: L.amount,
    uncategorized: L.uncategorized,
    total: L.total,
  };
  const wsExpenses = wb.addWorksheet(L.sheetExpenses);
  fillExpensesExcelSheet(wsExpenses, expenses, expensesSheetLabels);

  const purchasesSheetLabels = {
    ...defaultPurchasesLabels,
    sheetPurchases: L.sheetPurchases,
    totalPurchasesByCategory: L.totalPurchasesByCategory,
    productName: L.productName,
    category: L.category,
    unitType: L.unitType,
    supplier: L.supplier,
    date: L.date,
    quantity: L.quantity,
    unitPrice: L.unitPrice,
    unitSellingPrice: L.unitSellingPrice,
    oldCostPrice: L.oldCostPrice,
    oldSellingPrice: L.oldSellingPrice,
    inventoryBefore: L.inventoryBefore,
    inventoryAfter: L.inventoryAfter,
    totalPrice: L.totalPrice,
    uncategorized: L.uncategorized,
    total: L.total,
  };
  const wsPurchases = wb.addWorksheet(L.sheetPurchases);
  fillPurchasesExcelSheet(wsPurchases, purchases, purchasesSheetLabels, {
    formatUnit,
    categoryColorByName,
  });

  const refundsSheetLabels = {
    name: L.name,
    code: L.code ?? L.productCode ?? L.sku,
    category: L.category,
    refundedQty: L.refundedQty,
    refundedAmount: L.refundedAmount,
    uncategorized: L.uncategorized,
    offerCategory: L.offerCategory,
    total: L.total,
  };
  const wsRefunds = wb.addWorksheet(L.sheetRefunds);
  fillMonthlyClosingRefundsExcelSheet(wsRefunds, refunds || [], refundsSheetLabels, {
    categoryColorByName,
  });

  // ── Sales sheet (same layout / styling as Sales page export) ───────────────
  const wsSales = wb.addWorksheet(L.sheetSales);
  const salesSheetLabels = {
    ...defaultSalesLabels,
    sheetSales: L.sheetSales,
    productName: L.productName,
    productCode: L.productCode || L.sku,
    sku: L.sku,
    category: L.category,
    unitType: L.unitType,
    soldQty: L.soldQty,
    revenue: L.revenue,
    revenueProfit: L.revenueProfit,
    uncategorized: L.uncategorized,
    offerCategory: L.offerCategory,
    total: L.total,
  };
  fillProductSalesExcelSheet(wsSales, sales, salesSheetLabels, {
    formatUnit,
    categoryColorByName,
  });

  const inventorySheetLabels = {
    ...defaultInventoryLabels,
    sheetInventory: L.sheetInventory,
    productName: L.productName,
    sku: L.sku,
    category: L.category,
    unitType: L.unitType,
    costPrice: L.costPrice,
    sellingPrice: L.sellingPrice,
    stock: L.stock,
    uncategorized: L.uncategorized,
    total: L.total,
  };
  const wsInventory = wb.addWorksheet(L.sheetInventory);
  fillInventoryExcelSheet(wsInventory, inventory, inventorySheetLabels, {
    formatUnit,
    categoryColorByName,
  });

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

// ─── Customers export ──────────────────────────────────────────────────────────

const defaultCustomersLabels = {
  sheetCustomers: 'Customers',
  name:           'Name',
  phone:          'Phone',
  address:        'Address',
  totalOrders:    'Total Orders',
  totalSpent:     'Total Spent',
  total:          'Total',
};

export async function exportCustomersToExcel(customers, filename, labels = {}) {
  const L  = { ...defaultCustomersLabels, ...labels };
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(L.sheetCustomers);

  applyHeader(ws, [L.name, L.phone, L.address, L.totalOrders, L.totalSpent], [26, 18, 32, 16, 18]);

  let grandTotal = 0;
  let totalOrdersSum = 0;
  for (const c of customers || []) {
    const spent  = parseFloat(c.total_purchases || c.total_spent || 0);
    grandTotal  += spent;
    const orders = parseInt(c.order_count ?? 0, 10);
    totalOrdersSum += orders;
    const row = ws.addRow([
      c.name || c.full_name || '',
      c.phone   || '',
      c.address || '',
      orders,
      spent,
    ]);
    applyDataRow(row, 5);
    row.getCell(4).numFmt = '#,##0';
    row.getCell(5).numFmt = CURRENCY_FMT;
  }

  const customerCount = (customers || []).length;
  const totalRow = ws.addRow([`${L.total}: ${customerCount}`, '', '', totalOrdersSum, grandTotal]);
  applyTotalRow(totalRow, 5);
  totalRow.getCell(4).numFmt = '#,##0';
  totalRow.getCell(5).numFmt = CURRENCY_FMT;

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

// ─── Delivery Persons export ───────────────────────────────────────────────────

const defaultDeliveryLabels = {
  sheetDelivery: 'Delivery Personnel',
  name:          'Name',
  phone:         'Phone',
  total:         'Total',
};

export async function exportDeliveryPersonsToExcel(persons, filename, labels = {}) {
  const L  = { ...defaultDeliveryLabels, ...labels };
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(L.sheetDelivery);

  applyHeader(ws, [L.name, L.phone], [30, 22]);

  for (const p of persons || []) {
    const row = ws.addRow([p.name || '', p.phone || '']);
    applyDataRow(row, 2);
  }

  const totalRow = ws.addRow([`${L.total}: ${(persons || []).length}`, '']);
  applyTotalRow(totalRow, 2);

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

// ─── Audit Log export ──────────────────────────────────────────────────────────

const defaultAuditLabels = {
  sheetAudit:  'Audit Log',
  dateTime:    'Date/Time',
  user:        'User',
  role:        'Role',
  action:      'Action',
  entityType:  'Entity Type',
  entityId:    'Entity ID',
};

export async function exportAuditToExcel(logs, filename, labels = {}) {
  const L  = { ...defaultAuditLabels, ...labels };
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(L.sheetAudit);

  applyHeader(
    ws,
    [L.dateTime, L.user, L.role, L.action, L.entityType, L.entityId],
    [22, 20, 14, 30, 20, 16],
  );

  for (const e of logs || []) {
    const ts  = e.acted_at || e.created_at;
    const row = ws.addRow([
      ts ? new Date(ts).toLocaleString() : '',
      e.actor_name  || '',
      e.actor_role  || '',
      (e.action || '').replace(/_/g, ' '),
      e.entity_type || '',
      e.entity_id   || '',
    ]);
    applyDataRow(row, 6);
  }

  ws.autoFilter = { from: 'B1', to: 'F1' };

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}

// ─── Offers export ──────────────────────────────────────────────────────────

const defaultOffersLabels = {
  sheetOffers: 'Offers',
  sheetComponents: 'Offer Components',
  offerName: 'Offer Name',
  sku: 'SKU',
  previewCost: 'Preview Cost',
  sellingPrice: 'Selling Price',
  profitMargin: 'Profit Margin',
  status: 'Status',
  active: 'Active',
  inactive: 'Inactive',
  total: 'Total',
  productName: 'Product Name',
  productSku: 'Product SKU',
  quantity: 'Quantity',
  unitType: 'Unit Type',
  unitCost: 'Unit Cost',
  totalCost: 'Total Cost',
  noComponents: 'No components',
};

const OFFER_STATUS_STYLE = {
  active: { fill: 'FFDCFCE7', font: 'FF15803D' },
  inactive: { fill: 'FFFEE2E2', font: 'FFB91C1C' },
};

function paintOfferStatusCell(cell, isActive) {
  const key = isActive !== false ? 'active' : 'inactive';
  const st = OFFER_STATUS_STYLE[key];
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: st.fill } };
  cell.font = { bold: true, size: 11, color: { argb: st.font } };
  cell.border = ALL_BORDERS;
}

function paintProfitMarginCell(cell, marginRatio) {
  if (marginRatio == null || !Number.isFinite(marginRatio)) return;
  let fill;
  let fgArgb;
  if (marginRatio > 0.0005) {
    fill = 'FFDCFCE7';
    fgArgb = 'FF15803D';
  } else if (marginRatio < -0.0005) {
    fill = 'FFFEE2E2';
    fgArgb = 'FFB91C1C';
  } else {
    return;
  }
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  cell.font = { bold: true, size: 11, color: { argb: fgArgb } };
  cell.border = ALL_BORDERS;
}

function offerMarkupRatio(offer) {
  const selling = parseFloat(offer.selling_price ?? offer.price ?? 0);
  const cost = parseFloat(offer.preview_cost ?? 0);
  if (!Number.isFinite(selling) || !Number.isFinite(cost) || cost <= 0) return null;
  return (selling - cost) / cost;
}

function mergeVerticalCells(ws, col, startRow, endRow) {
  if (endRow <= startRow) return;
  ws.mergeCells(`${col}${startRow}:${col}${endRow}`);
  const cell = ws.getCell(`${col}${startRow}`);
  cell.alignment = { vertical: 'middle', horizontal: 'center' };
}

function paintOfferBlockOutline(ws, startRow, endRow, numCols = 8) {
  for (let r = startRow; r <= endRow; r++) {
    for (let c = 1; c <= numCols; c++) {
      const cell = ws.getCell(r, c);
      const prev = cell.border || {};
      cell.border = {
        top: r === startRow ? OUTLINE_BORDER : (prev.top ?? CELL_BORDER),
        bottom: r === endRow ? OUTLINE_BORDER : (prev.bottom ?? CELL_BORDER),
        left: c === 1 ? OUTLINE_BORDER : (prev.left ?? CELL_BORDER),
        right: c === numCols ? OUTLINE_BORDER : (prev.right ?? CELL_BORDER),
      };
    }
  }
}

/** Alternating offer-block background; skips columns (unit type, profit margin) to preserve their own colors. */
function paintOfferBlockAltFill(ws, startRow, endRow, numCols = 8, skipCols = [6, 10]) {
  const skip = new Set(skipCols);
  for (let r = startRow; r <= endRow; r++) {
    for (let c = 1; c <= numCols; c++) {
      if (skip.has(c)) continue;
      ws.getCell(r, c).fill = OFFER_BLOCK_ALT_FILL;
    }
  }
}

function fillOffersSummaryExcelSheet(ws, offers, L, formatStatus) {
  applyHeader(
    ws,
    [L.offerName, L.sku, L.status, L.previewCost, L.sellingPrice, L.profitMargin],
    [28, 16, 14, 16, 16, 14],
  );

  for (const offer of offers || []) {
    const selling = parseFloat(offer.selling_price ?? offer.price ?? 0);
    const cost = parseFloat(offer.preview_cost ?? 0);
    const marginRatio = offerMarkupRatio(offer);
    const row = ws.addRow([
      offer.name || '',
      offer.sku || '',
      formatStatus(offer.is_active),
      cost,
      selling,
      marginRatio != null ? marginRatio : '',
    ]);
    applyDataRow(row, 6);
    paintOfferStatusCell(row.getCell(3), offer.is_active);
    row.getCell(4).numFmt = CURRENCY_FMT;
    row.getCell(5).numFmt = CURRENCY_FMT;
    if (marginRatio != null) {
      row.getCell(6).numFmt = PCT_FMT;
      paintProfitMarginCell(row.getCell(6), marginRatio);
    }
  }

  const count = (offers || []).length;
  const totalRow = ws.addRow([`${L.total}: ${count}`, '', '', '', '', '']);
  applyTotalRow(totalRow, 6);

  ws.autoFilter = { from: 'A1', to: 'C1' };
}

function offerTotalCost(offer, items) {
  const preview = parseFloat(offer.preview_cost);
  if (Number.isFinite(preview)) return preview;
  return (items || []).reduce((sum, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const unitCost = parseFloat(item.cost_price) || 0;
    return sum + Math.round(qty * unitCost * 100) / 100;
  }, 0);
}

function fillOfferComponentsExcelSheet(ws, offers, L, { formatUnit }) {
  const numCols = 10;
  applyHeader(
    ws,
    [
      L.offerName,
      L.sku,
      L.productName,
      L.productSku,
      L.quantity,
      L.unitType,
      L.unitCost,
      L.totalCost,
      L.sellingPrice,
      L.profitMargin,
    ],
    [28, 16, 28, 16, 12, 14, 14, 14, 14, 14],
  );

  let nextRow = 2;
  let offerIndex = 0;

  for (const offer of offers || []) {
    const items = offer.items || offer.components || [];
    const offerName = offer.name || '';
    const offerSku = offer.sku || '';
    const totalCost = offerTotalCost(offer, items);
    const selling = parseFloat(offer.selling_price ?? offer.price ?? 0);
    const marginRatio = offerMarkupRatio({ ...offer, preview_cost: totalCost });
    const useAltFill = offerIndex % 2 === 0;

    if (!items.length) {
      const startRow = nextRow;
      const row = ws.addRow([
        offerName,
        offerSku,
        L.noComponents,
        '',
        '',
        '',
        '',
        totalCost,
        selling,
        marginRatio != null ? marginRatio : '',
      ]);
      applyDataRow(row, numCols);
      row.getCell(8).numFmt = CURRENCY_FMT;
      row.getCell(9).numFmt = CURRENCY_FMT;
      if (marginRatio != null) {
        row.getCell(10).numFmt = PCT_FMT;
        paintProfitMarginCell(row.getCell(10), marginRatio);
      }
      if (useAltFill) paintOfferBlockAltFill(ws, startRow, startRow, numCols);
      paintOfferBlockOutline(ws, startRow, startRow, numCols);
      nextRow += 1;
      offerIndex += 1;
      continue;
    }

    const startRow = nextRow;
    items.forEach((item, idx) => {
      const qty = parseFloat(item.quantity) || 0;
      const unitCost = parseFloat(item.cost_price) || 0;
      const unitRaw = item.unit_type;
      const unitDisplay = formatUnit(unitRaw);

      const row = ws.addRow([
        idx === 0 ? offerName : '',
        idx === 0 ? offerSku : '',
        item.product_name || '',
        item.sku || '',
        qty,
        unitDisplay,
        unitCost,
        idx === 0 ? totalCost : '',
        idx === 0 ? selling : '',
        idx === 0 && marginRatio != null ? marginRatio : '',
      ]);
      applyDataRow(row, numCols);
      row.getCell(5).numFmt = '#,##0.##';
      row.getCell(7).numFmt = CURRENCY_FMT;
      if (idx === 0) {
        row.getCell(8).numFmt = CURRENCY_FMT;
        row.getCell(9).numFmt = CURRENCY_FMT;
        if (marginRatio != null) {
          row.getCell(10).numFmt = PCT_FMT;
          paintProfitMarginCell(row.getCell(10), marginRatio);
        }
      }
      paintExcelUnitCell(row.getCell(6), unitRaw);
      nextRow += 1;
    });

    const endRow = nextRow - 1;
    mergeVerticalCells(ws, 'A', startRow, endRow);
    mergeVerticalCells(ws, 'B', startRow, endRow);
    mergeVerticalCells(ws, 'H', startRow, endRow);
    mergeVerticalCells(ws, 'I', startRow, endRow);
    mergeVerticalCells(ws, 'J', startRow, endRow);
    if (useAltFill) paintOfferBlockAltFill(ws, startRow, endRow, numCols);
    paintOfferBlockOutline(ws, startRow, endRow, numCols);
    offerIndex += 1;
  }

  ws.autoFilter = { from: 'A1', to: 'A1' };
}

export async function exportOffersToExcel(offers, filename, labels = {}) {
  const merged = { ...defaultOffersLabels, ...labels };
  const { formatStatus: formatStatusFn, formatUnitType: formatUnitTypeFn, ...L } = merged;
  const formatStatus =
    typeof formatStatusFn === 'function'
      ? formatStatusFn
      : (isActive) => (isActive !== false ? L.active : L.inactive);
  const formatUnit =
    typeof formatUnitTypeFn === 'function'
      ? formatUnitTypeFn
      : (unitType) => (unitType == null ? '' : String(unitType));

  const wb = new ExcelJS.Workbook();
  fillOffersSummaryExcelSheet(wb.addWorksheet(L.sheetOffers), offers, L, formatStatus);
  fillOfferComponentsExcelSheet(wb.addWorksheet(L.sheetComponents), offers, L, { formatUnit });

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);
}
